// Concise GPS indicator for the Assign Radios table (documentation/protocol.md §12). The age is
// always that of the GPS fix itself (last_location_at is the fix time, not when the server
// received it), and current GPS availability (gps_status from the radio's latest heartbeat) is
// shown separately: a stationary radio reports its position only every 5 minutes while its GPS
// is fine, so an older position alone does not mean the radio has lost GPS.

export function formatFixAge(lastFixAt: string, now: number = Date.now()): string {
  const ageMin = Math.floor((now - new Date(lastFixAt).getTime()) / 60000);
  if (ageMin < 1) return 'just now';
  if (ageMin < 60) return `${ageMin} min ago`;
  const ageHours = Math.floor(ageMin / 60);
  if (ageHours < 24) return `${ageHours} h ago`;
  return `${Math.floor(ageHours / 24)} d ago`;
}

export function formatLocationIndicator(
  lastFixAt: string | null,
  gpsStatus: string | null | undefined,
  now: number = Date.now(),
): string {
  const noFix = gpsStatus === 'not_available' || gpsStatus === 'invalid';
  if (!lastFixAt) return noFix ? 'No location · no GPS fix' : 'No location';
  const label = `Last GPS: ${formatFixAge(lastFixAt, now)}`;
  return noFix ? `${label} · no GPS fix now` : label;
}

/** A "GPS fix time" field shows only the fix's own time - never the receive time, the heartbeat
 * time or last_seen. Without one (rows that predate fix times) it says so. */
export function formatGpsFixTime(fixTime: string | null | undefined, format: (iso: string) => string): string {
  return fixTime ? format(fixTime) : 'Unknown (no GPS fix time recorded)';
}

/** Map popups: date and time of the fix plus its age, so an old fix never reads as current. */
export function formatMapFixTime(fixTime: string, now: number = Date.now()): string {
  const d = new Date(fixTime);
  const date = d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  return `${date} ${time} (${formatFixAge(fixTime, now)})`;
}
